import { INVOICE_ISSUE_DATE_MONTHS } from 'constants/formConstants'

import React, { useEffect, useState, useRef } from 'react'

import moment from 'moment'
import {
  AutocompleteInput,
  required,
  TextInput,
  useTranslate,
  DateInput,
} from 'react-admin'
import { useForm } from 'react-final-form'

import { CurrencyInput } from '../../inputs/currencyInput'

const monthsAgoDate = (date) => {
  const newDate = new Date(date)

  newDate.setMonth(date.getMonth() - INVOICE_ISSUE_DATE_MONTHS)

  return newDate
}

const getDateString = (date) => date.toISOString().slice(0, 10)

const getBrowserHandler = () => {
  if (window.safari) {
    return {
      dateFormat: 'DD/MM/YYYY',
      getDate: (value) => moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD'),
    }
  } else {
    return {
      dateFormat: 'YYYY-MM-DD',
      getDate: (value) => value,
    }
  }
}

const browserHandler = getBrowserHandler()

const validateDueDate = (t) => {
  return (dueDateString) => {
    const today = new Date()

    const selectedDueDate = moment(
      dueDateString,
      browserHandler.dateFormat,
      true
    )

    if (!selectedDueDate.isValid())
      return t('resources.invoices.messages.invalidDate')

    if (selectedDueDate.toDate().getTime() < today.getTime())
      return t('resources.invoices.messages.pastDate')
  }
}

const validateIssueDate = (t) => {
  return (issueDateString) => {
    const today = new Date()
    const fourMonthsAgo = monthsAgoDate(today)

    const selectedIssueDate = moment(
      issueDateString,
      browserHandler.dateFormat,
      true
    )
    if (!selectedIssueDate.isValid())
      return t('resources.invoices.messages.invalidDate')

    if (
      selectedIssueDate.toDate().getTime() >= today.getTime() ||
      selectedIssueDate.toDate().getTime() <= fourMonthsAgo.getTime()
    ) {
      return t('resources.invoices.messages.notLastMonth')
    }
  }
}

const priceBiggerThanZero = () => {
  return (amount) => {
    if (amount <= 0) {
      return 'resources.invoices.messages.biggerThanZero'
    }
  }
}

export const InvoiceCreationFormStep = ({
  className,
  locale,
  classes,
  data,
  onChangeImporterName,
  onChangeInvoice,
  onChangeIssueDate,
  onChangeDueDate,
  onChangeAmount,
  onChangePurchaseOrderNumber,
  setForm,
  selectedImporterID,
}) => {
  const today = new Date()
  const fourMonthsAgo = monthsAgoDate(today)
  const t = useTranslate()
  const form = useForm({
    mode: 'onChange',
  })
  const [selectedImporter, setSelectedImporter] = useState()
  const timer = useRef(null)

  useEffect(() => {
    setForm(form)
  }, [form, setForm])

  useEffect(() => {
    // this use effect is necessary so the component can update with the selected value AFTER
    // data is received
    if (selectedImporterID) {
      timer.current = setTimeout(() => {
        setSelectedImporter(selectedImporterID)
      }, 500)
    }

    return () => clearTimeout(timer.current)
  }, [selectedImporterID])

  const handleIssueDate = (event) => {
    if (!event) return
    const normalizedDate = moment(
      event.target.value,
      browserHandler.dateFormat,
      true
    )
    if (!normalizedDate.isValid()) return
    const normalizedOutput = normalizedDate.format('YYYY-MM-DD')

    if (onChangeIssueDate)
      onChangeIssueDate({
        ...event,
        target: {
          ...event.target,
          name: 'issueDate',
          value: normalizedOutput,
        },
      })
    form.change('issueDateText', event.target.value)
    form.change('issueDate', normalizedOutput)
  }

  const handleDueDate = (event) => {
    if (!event) return
    const normalizedDate = moment(
      event.target.value,
      browserHandler.dateFormat,
      true
    )
    if (!normalizedDate.isValid()) return
    const normalizedOutput = normalizedDate.format('YYYY-MM-DD')

    if (onChangeDueDate)
      onChangeDueDate({
        ...event,
        target: {
          ...event.target,
          name: 'dueDate',
          value: normalizedOutput,
        },
      })
    form.change('dueDateText', event.target.value)
    form.change('dueDate', normalizedOutput)
    handleDueDate()
  }

  const handleAmount = (_, value) => {
    if (onChangeAmount) onChangeAmount({ target: { name: 'amount', value } })
    form.change('amount', value)
  }

  return (
    <>
      <TextInput
        source="invoiceNumber"
        label="resources.invoices.actions.create.request.invoiceNumber"
        validate={required()}
        className={`${classes.firstColumn} ${className}`}
        onChange={onChangeInvoice}
      />
      <AutocompleteInput
        source="creditLineId"
        label="resources.invoices.actions.create.request.buyer"
        validate={required()}
        className={`${classes.secondColumn} ${className}`}
        choices={data.map((item) => ({
          id: item.businessProductId,
          name: item.importerName,
        }))}
        onChange={(value) =>
          onChangeImporterName({ target: { name: 'importerName', value } })
        }
        translateChoice={false}
        options={{
          fullWidth: false,
        }}
        defaultValue={selectedImporter}
      />
      {!window.safari ? (
        <DateInput
          source="issueDate"
          variant="filled"
          label={'resources.invoices.actions.create.request.issueDate'}
          type="date"
          className={`${classes.firstColumnDate} ${className}`}
          validate={[required(), validateIssueDate(t)]}
          onChange={handleIssueDate}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            min: getDateString(fourMonthsAgo),
            max: getDateString(today),
            placeholder: 'dd/mm/yyyy',
          }}
        />
      ) : (
        <TextInput
          source="issueDateText"
          label="resources.invoices.actions.create.request.issueDate"
          validate={[required(), validateIssueDate(t)]}
          className={`${classes.firstColumnDate} ${className}`}
          onChange={handleIssueDate}
          inputProps={{
            placeholder: 'dd/mm/yyyy',
          }}
        />
      )}
      {!window.safari ? (
        <DateInput
          source="dueDate"
          variant="filled"
          label={'resources.invoices.actions.create.request.dueDate'}
          type="date"
          className={`${classes.secondColumnDate} ${className}`}
          validate={[required(), validateDueDate(t)]}
          onChange={handleDueDate}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            min: getDateString(today),
            placeholder: 'dd/mm/yyyy',
          }}
        />
      ) : (
        <TextInput
          source="dueDateText"
          label="resources.invoices.actions.create.request.dueDate"
          validate={[required(), validateDueDate(t)]}
          className={`${classes.secondColumnDate} ${className}`}
          onChange={handleDueDate}
          inputProps={{
            placeholder: 'dd/mm/yyyy',
          }}
        />
      )}

      <CurrencyInput
        source="amount"
        label="resources.invoices.actions.create.request.invoiceAmount"
        validate={[required(), priceBiggerThanZero()]}
        className={`${classes.firstColumn} ${className} ${classes.amountColumn}`}
        onChange={handleAmount}
      />

      <TextInput
        source="purchaseOrderNumber"
        label="resources.invoices.actions.create.request.purchaseOrderNumber"
        className={`${classes.secondColumn} ${className}`}
        onChange={onChangePurchaseOrderNumber}
      />
      <TextInput
        source="language"
        className={`${classes.hiddenField} ${className}`}
        initialValue={locale}
        contentEditable={false}
      />
    </>
  )
}
